import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  Box,
  Alert,
  CircularProgress
} from '@mui/material';
import { adminService } from '../../services/adminService';

const UserDetailDialog = ({ user, open, onClose, onUserUpdated }) => {
  const [userDetail, setUserDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [editMode, setEditMode] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    title: '',
    department: '',
    company: '',
    industry: '',
    access_level: 'user',
    org_id: ''
  });
  const [orgOptions, setOrgOptions] = useState([]);
  const [orgLoading, setOrgLoading] = useState(false);
  const [orgError, setOrgError] = useState(null);

  useEffect(() => {
    if (open && user) {
      fetchUserDetail();
    }
  }, [open, user]);

  useEffect(() => {
    if (open && orgOptions.length === 0 && !orgLoading) {
      fetchOrganizations();
    }
  }, [open, orgOptions.length, orgLoading]);

  const fetchUserDetail = async () => {
    try {
      setLoading(true);
      setError(null);
      const detail = await adminService.getUserDetail(user.user_id);
      setUserDetail(detail);
      setFormData({
        email: detail.email || '',
        first_name: detail.first_name || '',
        last_name: detail.last_name || '',
        title: detail.title || '',
        department: detail.department || '',
        company: detail.company || '',
        industry: detail.industry || '',
        access_level: detail.access_level || 'user',
        org_id: detail.org_id || ''
      });
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchOrganizations = async () => {
    try {
      setOrgLoading(true);
      setOrgError(null);
      const orgs = await adminService.getOrganizations();
      setOrgOptions(orgs || []);
    } catch (error) {
      setOrgError(error.message || 'Failed to fetch organizations');
    } finally {
      setOrgLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      const payload = {
        ...formData,
        org_id: formData.org_id || null
      };
      await adminService.updateUser(user.user_id, payload);
      onUserUpdated(); // Refresh parent list
      onClose(); // Close dialog after successful save
    } catch (error) {
      setError(error.message);
    } finally {
      setSaving(false);
    }
  };


  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h6">
          Edit User: {user?.email}
        </Typography>
      </DialogTitle>
      
      <DialogContent dividers>
        {loading && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress />
            <Typography variant="body2" sx={{ mt: 2 }}>
              Loading user details...
            </Typography>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {orgError && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {orgError}
          </Alert>
        )}

        {userDetail && (
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Email"
                fullWidth
                variant="outlined"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Access Level"
                fullWidth
                variant="outlined"
                select
                value={formData.access_level}
                onChange={(e) => handleInputChange('access_level', e.target.value)}
                SelectProps={{ native: true }}
              >
                <option value="user">User</option>
                <option value="manager">Manager</option>
                <option value="admin">Admin</option>
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="First Name"
                fullWidth
                variant="outlined"
                value={formData.first_name}
                onChange={(e) => handleInputChange('first_name', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Last Name"
                fullWidth
                variant="outlined"
                value={formData.last_name}
                onChange={(e) => handleInputChange('last_name', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Title"
                fullWidth
                variant="outlined"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Department"
                fullWidth
                variant="outlined"
                value={formData.department}
                onChange={(e) => handleInputChange('department', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Company"
                fullWidth
                variant="outlined"
                value={formData.company}
                onChange={(e) => handleInputChange('company', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Industry"
                fullWidth
                variant="outlined"
                value={formData.industry}
                onChange={(e) => handleInputChange('industry', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Organization"
                fullWidth
                variant="outlined"
                value={formData.org_id}
                onChange={(e) => handleInputChange('org_id', e.target.value)}
                SelectProps={{ native: true }}
                helperText={orgLoading ? 'Loading organizations…' : 'Assign user to an existing organization'}
              >
                <option value="">No organization</option>
                {orgOptions.map((org) => (
                  <option key={org.org_id || org.id} value={org.org_id || org.id}>
                    {org.name || org.org_id || org.id}
                  </option>
                ))}
              </TextField>
            </Grid>
          </Grid>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button 
          onClick={handleSave} 
          variant="contained" 
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} /> : null}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserDetailDialog;
